/**
 * **주소 선택 위젯을 만지는 유일한 자리** — 다음(카카오) 우편번호 위젯과 지오코더의 전역
 * (`daum.Postcode` · `kakao.maps.services.Geocoder`)은 이 파일 밖에서 부르지 않는다.
 *
 * 종전에는 같은 `initDaum` 이 **다섯 화면에 복제**돼 있었다(매장 정보·사업장 정보·고객사 정보·고객사
 * 목록·고객사 트리). 다섯 사본 모두 위젯이 함께 주는 `bcode`(법정동 10자리)·`sigunguCode`(5자리)·
 * `sido`·`sigungu` 를 **버렸다** — 그 값이 필요해진 순간(플레이스 기준 주소·운영지역·매장 법정동 코드)
 * 사본마다 같은 줄을 더해야 했을 자리다. 그래서 「위젯을 열고 결과를 값으로 돌려준다」를 한 함수로 모았다.
 *
 * 이 모듈은 **경계**다: 전역·콜백·DOM 을 여기서만 만지고, 호출부는 `await` 한 값만 다룬다. 값에서
 * 무엇을 계산하는지는 순수 모듈(`utils/placeServiceArea.js`)이 답한다.
 *
 * 위젯 스크립트는 `public/index.html` 이 앱 부트 때 싣는다 — 여기서 동적으로 싣지 않는다.
 */

export const ADDRESS_PICKER_TEXT = Object.freeze({
  widgetMissing: '주소 검색을 불러오지 못했습니다. 잠시 뒤 다시 시도해 주세요.',
})

/** 위젯 스크립트가 실려 있는가 — 호출부가 버튼을 누르기 전에 물을 수 있다 */
export function isAddressPickerReady() {
  return typeof postcodeCtor() === 'function'
}

/**
 * 주소를 고르게 하고 그 결과를 돌려준다.
 *
 * - 사람이 고르지 않고 닫으면 **`null`** 로 resolve 한다(오류가 아니다 — 취소다).
 * - 위젯 스크립트가 없으면 reject 한다([ADDRESS_PICKER_TEXT.widgetMissing]).
 * - `geocode` 가 참(기본)이면 주소 → 좌표까지 묻고 `lat`·`lng` 를 채운다. 좌표를 못 얻으면 두 값은
 *   `null` 이고 나머지는 그대로다 — 좌표가 없다고 고른 주소를 버리지 않는다.
 *
 * @param {?{geocode: boolean}} options
 * @returns {Promise<?{address: string, zonecode: string, bcode: string, sigunguCode: string, sido: string, sigungu: string, lat: ?string, lng: ?string}>}
 */
export function pickAddress(options) {
  const wantsCoordinates = !options || options.geocode !== false

  return new Promise((resolve, reject) => {
    const Postcode = postcodeCtor()
    if (typeof Postcode !== 'function') {
      reject(new Error(ADDRESS_PICKER_TEXT.widgetMissing))

      return
    }

    let picked = null
    let settled = false
    const settle = (value) => {
      if (settled) return
      settled = true
      resolve(value)
    }

    new Postcode({
      oncomplete: function (data) {
        picked = toPicked(data)
        if (!wantsCoordinates) {
          settle(picked)

          return
        }
        geocode(picked.address, (coordinates) => settle(Object.assign({}, picked, coordinates)))
      },
      // 고른 것이 있으면 좌표 조회가 끝난 뒤에 답한다 — 닫힘이 그 답을 앞지르지 않게 한다
      onclose: function () {
        if (!picked) settle(null)
      },
    }).open()
  })
}

/** 위젯 결과에서 우리가 쓰는 값만 — 위젯 객체를 화면으로 흘려보내지 않는다 */
function toPicked(data) {
  const row = data || {}

  return {
    address: row.address || '',
    zonecode: row.zonecode || '',
    bcode: row.bcode || '',
    sigunguCode: row.sigunguCode || '',
    sido: row.sido || '',
    sigungu: row.sigungu || '',
    lat: null,
    lng: null,
  }
}

/**
 * 주소 → 좌표. 지오코더가 없거나 못 찾으면 `{lat: null, lng: null}` 로 **반드시 답한다** —
 * 답하지 않는 갈래가 하나라도 있으면 [pickAddress] 의 약속이 영원히 미해결로 남는다.
 */
function geocode(address, done) {
  const services = mapsServices()
  if (!address || !services || typeof services.Geocoder !== 'function') {
    done({ lat: null, lng: null })

    return
  }

  new services.Geocoder().addressSearch(address, function (result, status) {
    const ok = services.Status && status === services.Status.OK && Array.isArray(result) && result.length > 0
    done(ok ? { lat: result[0].y, lng: result[0].x } : { lat: null, lng: null })
  })
}

function postcodeCtor() {
  const root = globalScope()

  return root && root.daum ? root.daum.Postcode : undefined
}

function mapsServices() {
  const root = globalScope()

  return root && root.kakao && root.kakao.maps ? root.kakao.maps.services : undefined
}

function globalScope() {
  return typeof window === 'undefined' ? undefined : window
}
